import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiLayers, FiTarget, FiAward, FiArrowRight, FiCheckCircle } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import LoadingSpinner from '../common/LoadingSpinner';
import { BRAND, Page, PageHeader, Card, Tag, ProgressBar, Segmented, EmptyState, Alert, buttonStyle, fadeUp, stagger } from '../common/ui';

const HERO = { img: '/illustrations/lesson1/scenes/challenge.jpg', pos: 'center 25%', height: 640, opacity: 0.6 };

// Quiz.order mirrors the lesson order (0-based) → the deck's first chapter cover.
function lessonCover(quiz) {
  const n = Number.isInteger(quiz?.order) ? quiz.order + 1 : 1;
  return `/illustrations/lesson${n}/scenes/cover-c1.jpg`;
}

function quizIdFromAttempt(qa) {
  if (!qa?.quiz) return null;
  return typeof qa.quiz === 'object' ? qa.quiz._id : qa.quiz;
}

function summarizeAttemptsForQuiz(quizAttempts, quizId) {
  const id = String(quizId);
  const list = (quizAttempts || []).filter((a) => String(quizIdFromAttempt(a)) === id);
  if (!list.length) return { attempted: false, passed: false, bestPercentage: null };
  const passed = list.some((a) => a.passed);
  const bestPercentage = Math.max(...list.map((a) => Number(a.percentage) || 0));
  return { attempted: true, passed, bestPercentage };
}

export default function QuizList() {
  const { user } = useAuth();
  const [quizzes, setQuizzes] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isAdmin = user?.role === 'admin';
  const courseId = user?.course?._id || user?.course;
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [coursesLoaded, setCoursesLoaded] = useState(!isAdmin);

  useEffect(() => {
    if (!isAdmin) return;
    api.get('/courses').then(res => {
      const list = res.data?.courses || res.data || [];
      setCourses(list);
      if (list.length > 0) setSelectedCourse(list[0]._id);
      setCoursesLoaded(true);
    }).catch(() => { setCoursesLoaded(true); });
  }, [isAdmin]);

  const activeCourseId = isAdmin ? selectedCourse : courseId;

  useEffect(() => {
    if (!coursesLoaded) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      if (!activeCourseId) {
        setQuizzes([]);
        setProgress(null);
        setLoading(false);
        return;
      }
      try {
        const requests = [api.get(`/quizzes/course/${activeCourseId}`)];
        if (!isAdmin) requests.push(api.get('/progress/my'));
        const results = await Promise.all(requests);
        if (!cancelled) {
          setQuizzes(results[0].data?.quizzes || []);
          setProgress(!isAdmin ? (results[1]?.data?.progress || null) : null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(
            e.response?.data?.error ||
              "Oops! We couldn't load the quizzes. Try again in a moment!"
          );
          setQuizzes([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [activeCourseId, isAdmin, coursesLoaded]);

  // ---- presentation helpers (same dark CORE scene for teachers and students) ----
  const pageProps = { hero: HERO };
  const mutedColor = BRAND.muted;

  const rows = quizzes.map((quiz, idx) => ({
    quiz,
    idx,
    qCount: (quiz.questions || []).length,
    ...summarizeAttemptsForQuiz(progress?.quizAttempts, quiz._id),
  }));
  const passedCount = rows.filter(r => r.passed).length;

  const courseSwitcher = isAdmin && courses.length > 1 ? (
    <Segmented
      value={selectedCourse}
      onChange={setSelectedCourse}
      options={courses.map(c => ({ value: c._id, label: `${c.icon ? `${c.icon} ` : ''}${c.title}` }))}
    />
  ) : null;

  const header = (
    <PageHeader
      kicker={isAdmin ? 'Quiz manager' : 'Test yourself'}
      title="Quiz"
      subtitle={
        isAdmin
          ? `${quizzes.length} ${quizzes.length === 1 ? 'quiz' : 'quizzes'} in this course — open one to preview it.`
          : rows.length
            ? `${passedCount} of ${rows.length} passed — ${passedCount === rows.length ? 'you cleared them all!' : 'show Trace what you remember.'}`
            : 'Pick a quiz and prove what you learned.'
      }
      right={courseSwitcher}
    />
  );

  if (loading) {
    return (
      <Page {...pageProps}>
        <LoadingSpinner message="Loading quizzes…" />
      </Page>
    );
  }

  if (!activeCourseId && !isAdmin) {
    return (
      <Page {...pageProps}>
        {header}
        <EmptyState
          icon="🎒"
          title="No course assigned"
          text="You're not enrolled in a course yet. Ask your teacher to connect you to a course to see the quizzes!"
        />
      </Page>
    );
  }

  if (error) {
    return (
      <Page {...pageProps}>
        {header}
        <Alert>{error}</Alert>
      </Page>
    );
  }

  if (!quizzes.length) {
    return (
      <Page {...pageProps}>
        {header}
        <EmptyState
          icon="✨"
          title="No quizzes here yet!"
          text="Check back later — your teacher may add new ones."
        />
      </Page>
    );
  }

  return (
    <Page {...pageProps}>
      {header}

      <motion.div
        variants={stagger}
        initial="hidden"
        animate="visible"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))',
          gap: 18,
        }}
      >
        {rows.map(({ quiz, idx, qCount, attempted, passed, bestPercentage }) => {
          const qid = quiz._id;
          const num = String(idx + 1).padStart(2, '0');
          const cta = isAdmin ? 'Preview' : passed ? 'Replay' : attempted ? 'Retry' : 'Start';
          const ctaOutline = passed && !isAdmin;
          const ctaStyle = {
            ...buttonStyle(ctaOutline ? 'outline' : 'gold', 'sm'),
            pointerEvents: 'none',
          };

          return (
            <motion.div key={qid} variants={fadeUp} whileHover={{ y: -6 }} whileTap={{ scale: 0.99 }}>
              <Link
                to={isAdmin ? `/admin/quiz/${qid}` : `/quiz/${qid}`}
                style={{ display: 'block', height: '100%', textDecoration: 'none', color: 'inherit' }}
              >
                <Card style={{ position: 'relative', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column', padding: 0 }}>
                  {/* lesson scene strip: the deck's own chapter cover */}
                  <div aria-hidden style={{ position: 'relative', height: 132, overflow: 'hidden', background: BRAND.navy800 }}>
                    <img src={lessonCover(quiz)} alt="" loading="lazy"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 30%', display: 'block', transform: 'scale(1.04)' }} />
                    <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(180deg, rgba(7,20,45,0.05) 0%, rgba(7,20,45,0.35) 55%, ${BRAND.navy800} 100%)` }} />
                    <span style={{ position: 'absolute', top: 6, right: 14, fontSize: '4rem', fontWeight: 900, lineHeight: 1, color: 'rgba(249,240,231,0.22)', textShadow: '0 2px 18px rgba(0,0,0,0.5)' }}>{num}</span>
                  </div>

                  <div style={{ padding: '14px 20px 18px', display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12, position: 'relative' }}>
                    {!isAdmin && passed && <Tag tone="success"><FiCheckCircle size={12} /> Passed</Tag>}
                    {!isAdmin && !passed && attempted && <Tag tone="solid">Try again</Tag>}
                    {!isAdmin && !attempted && <Tag tone="muted">New</Tag>}
                    {isAdmin && <Tag tone="gold">Quiz {num}</Tag>}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6, position: 'relative' }}>
                    <span style={{ fontSize: '2.2rem', fontWeight: 900, lineHeight: 1, color: BRAND.goldLight }}>{qCount}</span>
                    <span style={{ fontSize: '0.78rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: mutedColor }}>
                      {qCount === 1 ? 'question' : 'questions'}
                    </span>
                  </div>

                  <h2 style={{ fontSize: '1.15rem', fontWeight: 900, lineHeight: 1.25, margin: '0 0 6px', position: 'relative' }}>
                    {quiz.title}
                  </h2>
                  <p style={{ fontSize: '0.88rem', color: mutedColor, lineHeight: 1.45, margin: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {quiz.description || 'Ready for the challenge?'}
                  </p>

                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12, fontSize: '0.76rem', color: mutedColor, fontWeight: 700 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><FiLayers size={13} /> {qCount} questions</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><FiTarget size={13} /> Pass at {quiz.passingScore ?? 60}%</span>
                    {attempted && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: BRAND.goldLight }}><FiAward size={13} /> Best {bestPercentage}%</span>
                    )}
                  </div>

                  <div style={{ marginTop: 'auto', paddingTop: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <ProgressBar value={attempted ? bestPercentage : 0} height={5} style={{ flex: 1 }} />
                    <span style={ctaStyle}>
                      {cta} <FiArrowRight size={14} />
                    </span>
                  </div>
                  </div>
                </Card>
              </Link>
            </motion.div>
          );
        })}
      </motion.div>
    </Page>
  );
}
